/**
 * Run-of-show export — the itinerary in chronological order, times rendered
 * in the trip's own zone.
 *
 * Time formatting is not re-derived here. `formatRangeInZone` (lib/tripTime.ts)
 * already owns the DST-safe, zone-suffixed rendering the Itinerary tab uses;
 * a second implementation here would be a second place for "7:30 PM" to
 * quietly become the browser's zone instead of the trip's.
 *
 * Ordering sorts on the actual instant (`Date.parse`), never on the ISO
 * string. Two items can carry different UTC offsets — a per-item zone
 * override, or dates either side of a DST change — and string order and
 * instant order disagree exactly then.
 */
import { formatRangeInZone } from '../tripTime';
import { itineraryStatus, ITINERARY_STATUS_LABELS } from '../itineraryVisibility';
import type { GroupItineraryItem } from '../../types/groups';
import { toCsv } from './csv';

export interface RunOfShowRow {
  time: string;
  title: string;
  location: string;
  status: string;
  notes: string;
}

export const RUN_OF_SHOW_HEADERS = ['Time', 'Event', 'Location', 'Status', 'Notes'] as const;

/** Sortable instant, or +Infinity for anything with no usable start time. */
function instantMs(item: Pick<GroupItineraryItem, 'startsAt'>): number {
  if (!item.startsAt) return Number.POSITIVE_INFINITY;
  const ms = new Date(item.startsAt).getTime();
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
}

/**
 * Every item, earliest first. Items with no start time sort to the end, in
 * their own `displayOrder` among themselves rather than being dropped — a
 * run of show is a checklist as much as a clock, and an item a host has not
 * timed yet is still something they need to see on the page.
 */
export function chronologicalItineraryItems(
  items: readonly GroupItineraryItem[],
): GroupItineraryItem[] {
  return [...items].sort((a, b) => {
    // Two undated items both resolve to +Infinity, and Infinity - Infinity is
    // NaN — not a valid comparator result, and V8 treats it as "equal" only
    // by accident. Compare the instants explicitly instead of by subtraction
    // so the undated/undated case falls straight through to displayOrder.
    const aMs = instantMs(a);
    const bMs = instantMs(b);
    if (aMs < bMs) return -1;
    if (aMs > bMs) return 1;
    return a.displayOrder - b.displayOrder;
  });
}

/**
 * The Notes column. A cancelled item's reason is the one thing a host on the
 * ground most needs to see next to it — the Itinerary tab shows it, and a
 * run of show that printed "Cancelled" with a blank note would have the host
 * guessing why.
 */
export function runOfShowNotes(
  item: Pick<GroupItineraryItem, 'description' | 'cancellationReason' | 'status'>,
): string {
  const reason = itineraryStatus(item) === 'cancelled' ? String(item.cancellationReason ?? '').trim() : '';
  const description = String(item.description ?? '').trim();
  if (reason && description) return `Cancelled: ${reason} — ${description}`;
  if (reason) return `Cancelled: ${reason}`;
  return description;
}

export function runOfShowRows(
  items: readonly GroupItineraryItem[],
  tripTimeZone: string,
): RunOfShowRow[] {
  return chronologicalItineraryItems(items).map((item) => ({
    time: formatRangeInZone(item.startsAt, item.endsAt, item.timeZone || tripTimeZone, 'Time TBD'),
    title: item.title || 'Untitled',
    location: item.locationName || '',
    status: ITINERARY_STATUS_LABELS[itineraryStatus(item)],
    notes: runOfShowNotes(item),
  }));
}

export function runOfShowCsv(items: readonly GroupItineraryItem[], tripTimeZone: string): string {
  const rows = runOfShowRows(items, tripTimeZone);
  return toCsv(
    RUN_OF_SHOW_HEADERS,
    rows.map((row) => [row.time, row.title, row.location, row.status, row.notes]),
  );
}
